import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/base/Seo";
import VideoCard from "@/components/feature/VideoCard";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { faqs } from "@/mocks/site";

export default function VideoDetailPage() {
  const { id } = useParams();
  const video = videos.find((v) => v.id === id);
  const [liked, setLiked] = useState(false);

  const related = useMemo(
    () => videos.filter((v) => v.categoryId === video?.categoryId && v.id !== video?.id).slice(0, 4),
    [video]
  );

  if (!video) {
    return (
      <Layout>
        <Seo title="视频不存在" description="未找到对应视频，请返回首页继续浏览娱乐圈新鲜事。" />
        <div className="mx-auto flex max-w-7xl flex-col items-center px-4 py-24 text-center">
          <i className="ri-video-off-line text-5xl text-primary-400" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">视频不存在或已下线</h1>
          <Link to="/" className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const cat = categories.find((c) => c.id === video.categoryId);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: video.title,
    description: video.summary,
    thumbnailUrl: video.cover,
    uploadDate: "2026-08-18",
    contentUrl: `https://soulhandmade.cn/videos/${video.id}`,
    author: { "@type": "Organization", name: "吃瓜视频网" },
  };

  return (
    <Layout>
      <Seo
        title={video.title}
        description={video.summary}
        keywords={`${cat?.name},${video.tags.join(",")},娱乐八卦,圈内速递`}
        canonical={`https://soulhandmade.cn/videos/${video.id}`}
        type="video.other"
        jsonLd={articleJsonLd}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/columns/${cat?.slug}`} className="hover:text-primary-600">{cat?.name}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="truncate text-foreground-700">{video.title}</span>
        </nav>

        <div className="mt-6 grid gap-8 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2">
            <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-foreground-950">
              <img src={video.cover} alt={`${video.title} 视频截图`} className="h-full w-full object-cover opacity-90" />
              <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/30">
                <button
                  type="button"
                  onClick={() => setLiked((v) => v)}
                  className="flex h-16 w-16 items-center justify-center rounded-full bg-background-50/95 text-primary-600 transition hover:scale-105"
                  aria-label="播放视频"
                >
                  <i className="ri-play-fill text-3xl" />
                </button>
              </div>
              <span className="absolute bottom-3 right-3 rounded bg-foreground-950/70 px-2 py-0.5 text-xs text-background-50">
                {video.duration}
              </span>
            </div>

            <div className="mt-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">{cat?.name}</span>
                {video.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/search?q=${encodeURIComponent(t)}`}
                    className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600 transition hover:text-primary-600"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
              <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
                {video.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3 text-sm text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-fire-line" />
                    </span>
                    {video.author}
                  </span>
                  <span className="hidden sm:inline">·</span>
                  <span>{video.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setLiked((v) => !v)}
                    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition ${
                      liked ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
                    {liked ? "已赞" : "点赞"} {video.likes.toLocaleString()}
                  </button>
                  <button
                    type="button"
                    onClick={() => setLiked((v) => v)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-4 py-2 text-sm font-medium text-foreground-600 transition hover:text-primary-600"
                  >
                    <i className="ri-share-line" /> 分享
                  </button>
                </div>
              </div>

              <div className="mt-6 rounded-xl bg-background-100 p-5 ring-1 ring-inset ring-background-200">
                <h2 className="text-sm font-semibold text-foreground-900">视频简介</h2>
                <p className="mt-3 text-sm leading-7 text-foreground-600">{video.summary}</p>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-background-200 pt-4 text-sm text-foreground-600 sm:grid-cols-4">
                  <span className="flex items-center gap-1.5"><i className="ri-eye-line text-primary-500" /> 播放 {video.views}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-thumb-up-line text-primary-500" /> {video.likes.toLocaleString()}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-chat-3-line text-primary-500" /> {video.comments.toLocaleString()}</span>
                  <span className="flex items-center gap-1.5"><i className="ri-time-line text-primary-500" /> {video.duration}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div>
              <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-play-list-line text-primary-600" /> 相关推荐
              </h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-1">
                {related.map((v) => (
                  <VideoCard key={v.id} video={v} />
                ))}
              </div>
            </div>

            <div className="rounded-xl bg-background-100 p-5 ring-1 ring-inset ring-background-200">
              <h2 className="text-sm font-semibold text-foreground-900">栏目介绍</h2>
              <p className="mt-2 text-sm leading-6 text-foreground-600">{cat?.desc}</p>
              <Link
                to={`/columns/${cat?.slug}`}
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                进入{cat?.name}栏目 <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>

        {/* FAQ */}
        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950">观看常见问题</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {faqs.slice(0, 4).map((f) => (
              <div key={f.q} className="rounded-xl bg-background-100 p-5 ring-1 ring-inset ring-background-200">
                <h3 className="text-sm font-semibold text-foreground-900">{f.q}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}